/*
 * Byte-identical in Cortex and Quantum.
 *
 * A GENERAL CAMPAIGN DOES NOT PUBLISH THE THEATRICAL TEMPLATE.
 *
 * The strategy page's template copy was written for film: trailer and
 * showtime language, Buy Tickets traffic, cast and director discovery,
 * release milestones, opening weekend, "theatrical" naming. The backend
 * rewrites it for the declared verticals it knows
 * (backend utils/verticalMicrositeLanguage.js), film is its home, and a
 * campaign that declares no category — the neutral General taxonomy — was
 * served it unchanged. Campaign 55 (OutKick, YouTube / CTV / Display) would
 * have been told to "Launch Search" and to watch "Buy Tickets page traffic".
 *
 * For a General campaign, and only for one:
 *   - the sections that are theatrical by construction, with no
 *     category-neutral equivalent, are withheld (GENERAL_CAMPAIGN_WITHHELD_SECTIONS,
 *     as proposal mode withholds sections for campaign 53), and their data is
 *     dropped so that no other module quotes it; so is a release-phase read
 *     the backend built as theatrical;
 *   - in the sections the page still shows, a template item (a card, a theme,
 *     a milestone, a guardrail, a trigger) that carries film language is
 *     removed whole, and in the campaign's own rows (its audiences, platforms,
 *     KPIs, budget splits, ad sets) only the film line goes: a row goes only
 *     when its own name was film template (the default "Core Moviegoing
 *     Audience"). Ad-set names lose the backend's THEATRICAL token. Nothing
 *     is reworded.
 *
 * Film language is what the backend's own overlay rewrites for a declared
 * vertical (FILM_TEMPLATE_PATTERNS), plus the film release phrases it does not.
 * A film term is the campaign's own only where it sits inside a verbatim,
 * whole-word occurrence of one of the campaign's saved inputs (its name,
 * advertiser, objective, KPI, notes and targeting), never the generated
 * strategy text: a sports campaign that sells tickets keeps its ticket KPI
 * wherever the template quotes it, and nothing else.
 *
 * Never touched: the saved strategy text, the campaign's own fields, the
 * hero, the positioning statement (the page already words it for General),
 * allocations, charts and every number. Film and each declared vertical get
 * the campaign back unchanged: the same object.
 */

// Page sections (the page's showSection keys) withheld for a General campaign.
export const GENERAL_CAMPAIGN_WITHHELD_SECTIONS = Object.freeze([
  "earnedMedia", // Buy Tickets traffic, cast / director discovery, IMDb / TMDb
  "releaseMilestones", // trailer signal read, screenings, theater geos, opening weekend
  "retargeting", // trailer completers, showtime abandoners, ticketing tiers
  "actionItems", // ticket / trailer / showtime events, a Search launch, film press, per-screen expansion
]);

// The payload sections behind them, dropped from both places the payload
// carries a section (campaign.sections.<key> and campaign.<key>), and their
// copies in the payload's chart-ready projection (campaign.chartReady).
const WITHHELD_PAYLOAD_SECTIONS = Object.freeze([
  "earnedMediaIntelligence",
  "releaseMilestoneSchedule",
  "retargetingArchitecture",
  "actionItems",
]);
const WITHHELD_CHART_READY_KEYS = Object.freeze(["earnedMediaSignals", "releaseMilestones"]);

// The template sections the page still shows. copyItems lists the arrays whose
// entries are self-contained template items, removed whole when they carry
// film language ("*": every array of objects in the section). Every other
// array holds the campaign's own rows. keep lists lines left as served.
const TEMPLATE_SECTIONS = Object.freeze({
  strategyOverview: { copyItems: ["cards"], keep: ["positioningStatement", "overview"] },
  // The planning-weights model is the campaign's own planner inputs,
  // normalized (backend utils/strategyPlanningWeights.js): it carries no
  // template copy, and its labels are pieces of the saved inputs, which the
  // whole-input test below cannot see. Filtering it would drop rows and leave
  // weights that no longer total 100.0, so it is kept as served.
  audienceIntelligence: { copyItems: [], keep: ["planningWeights"] },
  budgetArchitecture: { copyItems: [] },
  benchmarkJustification: { copyItems: ["intelligence.cards"] },
  campaignDetailSection: { copyItems: [] },
  campaignDetails: { copyItems: [] }, // the payload's second copy of campaignDetailSection
  chartReady: { copyItems: ["creativeThemes"] },
  creativeMessaging: { copyItems: ["themes"] },
  optimizationMeasurement: { copyItems: [] },
  executionModules: { copyItems: [] },
  antiWasteGuardrails: { copyItems: "*" },
  scenarioExpansionTriggers: { copyItems: "*" },
  successMetrics: { copyItems: ["summaryStats"] },
  launchActivationCalendar: { copyItems: ["milestones"] },
  quickReference: { copyItems: [] },
  releasePhaseIntelligence: { copyItems: [] },
});

/*
 * The film concepts the backend overlay rewrites for a declared vertical
 * (backend utils/verticalMicrositeLanguage.js at 7a718d23: SHARED_ENTERTAINMENT_RULES,
 * the entertainment → retail NATIVE_RULES, TOKEN_RULES), then the film release
 * and title phrases it leaves alone.
 */
const FILM_TEMPLATE_PATTERNS = Object.freeze([
  String.raw`\btrailer[-\s]plays?\b`,
  String.raw`\btrailer[-\s]views?\b`,
  String.raw`\btrailer completions?\b`,
  String.raw`\btrailer completers?\b`,
  String.raw`\btrailer viewers?\b`,
  String.raw`\btrailer[-\s]first\b`,
  String.raw`\btrailer[-\s]native\b`,
  String.raw`\btrailer intrigue\b`,
  String.raw`\btrailer exposure\b`,
  String.raw`\btheatrical release\b`,
  String.raw`\bopening[-\s]window\b`,
  String.raw`\brelease[-\s]message\b`,
  String.raw`\brelease milestones?\b`,
  String.raw`\bbox office lift\b`,
  String.raw`\bbox office\b`,
  String.raw`\bcinema markets?\b`,
  String.raw`\bcinemas?\b`,
  String.raw`\bscreenings?\b`,
  String.raw`\bpremieres?\b`,
  String.raw`\bnow[-\s]playing\b`,
  String.raw`\bcore moviegoing audiences?\b`,
  String.raw`\bmoviegoing\b`,
  String.raw`\btitle intrigue\b`,
  String.raw`\btitle memory\b`,
  String.raw`\btitle interest\b`,
  String.raw`\blocal-market title\b`,
  String.raw`\bentertainment campaigns\b`,
  String.raw`\bcritic validation\b`,
  String.raw`\bruntime\/context details\b`,
  String.raw`\bcast\s*\/\s*director discovery\b`,
  String.raw`\bcast and director\b`,
  String.raw`\bcast\s*\/\s*director\b`,
  String.raw`\bcast list, director, official site\b`,
  String.raw`\bmain cast list\b`,
  String.raw`\bmain cast\b`,
  String.raw`\bcast lists?\b`,
  String.raw`\bdirector name\b`,
  String.raw`\bIMDb\/TMDb\/Rotten Tomatoes-style metadata\b`,
  String.raw`\bIMDb\/TMDb metadata\b`,
  String.raw`\bIMDb\b`,
  String.raw`\bTMDb\b`,
  String.raw`\bRotten Tomatoes\b`,
  String.raw`\bEPK \/ press kit text\b`,
  String.raw`\bEPK\b`,
  String.raw`\bmovies?\b`,
  String.raw`\bfilms?\b`,
  String.raw`\bticket[-\s]purchase lifts?\b`,
  String.raw`\bticket transactions?\b`,
  String.raw`\bticket sales\b`,
  String.raw`\bticket[-\s]clicks?\b`,
  String.raw`\bticket availability\b`,
  String.raw`\bticketing\b`,
  String.raw`\btickets\b`,
  String.raw`\bticket\b`,
  String.raw`\btrailer cuts?\b`,
  String.raw`\btrailers?\b`,
  String.raw`\bshowtime[-\s]views?\b`,
  String.raw`\blocal showtimes?\b`,
  String.raw`\bshowtimes?\b`,
  String.raw`\bopening weekend\b`,
  String.raw`\btheatrical launche?s?\b`,
  String.raw`\btheatrical intent\b`,
  String.raw`\btheatrical awareness\b`,
  String.raw`\brelease[-\s]windows?\b`,
  String.raw`\bper[-\s]screen\b`,
  String.raw`\bbranded title queries\b`,
  String.raw`\bmoviegoers?\b`,
  String.raw`\btheatrical\b`,
  String.raw`THEATRICAL`,
  String.raw`\bcinematic(?:ally)?\b`,
  String.raw`\bstaged release\b`,
  String.raw`\brelease (?:event|strategy|timing)\b`,
  String.raw`\bholdovers?\b`,
  String.raw`\btitle[-\s]context\b`,
  String.raw`\btitle(?:['’]s)?\s+(?:strongest|prestige)\b`,
  String.raw`\bthe title\b`,
  String.raw`\bgruvi\b`,
]);
export const THEATRICAL_TEMPLATE_LANGUAGE = new RegExp(FILM_TEMPLATE_PATTERNS.join("|"), "i");
const FILM_TERMS = new RegExp(FILM_TEMPLATE_PATTERNS.join("|"), "gi");

// Keys whose values are identity or data, never template copy.
const IDENTITY_KEY = /^(?:key|keys|id|ids|slug|url|href|path|link|token|hash|platform|platformKey|status|state|tone|accent|color|type|kind)$|(?:Key|Keys|Id|Ids|Slug|Url|Href|Path|Link|Token|Hash|Date|At)$|_(?:key|id|slug|url|path|date|at)$/;
const NAME_KEY = /^(?:label|title|name|heading|phase)$/;
const UNIFIED_LABEL_KEY = "unifiedLabel";
const THEATRICAL_LABEL_TOKEN = /(^|_)THEATRICAL(?=_|$)/g;

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const campaignReceivesGeneralTemplate = (advertiserVertical) => advertiserVertical === "general";

// Where the payload carries the campaign's saved inputs. The generated
// strategy text is Cphere's, not the campaign's.
const CAMPAIGN_INPUT_FIELDS = Object.freeze([
  "campaignName",
  "campaign_name",
  "advertiserName",
  "advertiser_name",
  "advertiser",
  "objective",
  "primaryKpi",
  "primary_kpi",
  "notes",
  "targeting",
]);

const normalizeText = (value) => String(value).replace(/\s+/g, " ").trim();

// The campaign's saved inputs, whole and in their parts (a list of teams, a KPI
// and its clauses), in lower case.
const campaignInputValues = (campaign) => {
  const values = new Set();
  const add = (value) => {
    const whole = normalizeText(value).toLowerCase();
    if (!/[a-z]/.test(whole)) return;
    values.add(whole);
    for (const part of whole.split(/\s*(?:[;\n]|,\s|\s[—–]\s|\.\s)\s*/)) {
      const clean = part.replace(/^(?:and|or)\s+/, "").replace(/[.,;:]+$/, "").trim();
      if (clean.length >= 3 && /[a-z]/.test(clean)) values.add(clean);
    }
  };
  const walk = (value) => {
    if (typeof value === "string") add(value);
    else if (Array.isArray(value)) value.forEach(walk);
    else if (isPlainObject(value)) Object.values(value).forEach(walk);
  };
  for (const key of CAMPAIGN_INPUT_FIELDS) walk(campaign?.[key]);
  return [...values];
};

// The spans of a text that are verbatim, whole-word occurrences of a saved input.
const isWordCharacter = (character) => /[a-z0-9]/i.test(character || "");
const campaignInputSpans = (text, inputs) => {
  const lower = text.toLowerCase();
  const spans = [];
  for (const value of inputs) {
    for (let at = lower.indexOf(value); at >= 0; at = lower.indexOf(value, at + 1)) {
      const end = at + value.length;
      if (!isWordCharacter(lower[at - 1]) && !isWordCharacter(lower[end])) spans.push([at, end]);
    }
  }
  return spans;
};

// Film template language: a film term outside every occurrence of the
// campaign's saved inputs.
const carriesFilmTemplate = (value, own) => {
  if (typeof value === "string") {
    const text = normalizeText(value);
    FILM_TERMS.lastIndex = 0;
    let spans = null;
    let match;
    while ((match = FILM_TERMS.exec(text))) {
      if (!match[0].length) {
        FILM_TERMS.lastIndex += 1;
        continue;
      }
      spans = spans || campaignInputSpans(text, own);
      const start = match.index;
      const end = start + match[0].length;
      if (!spans.some(([from, to]) => from <= start && end <= to)) return true;
    }
    return false;
  }
  if (Array.isArray(value)) return value.some((entry) => carriesFilmTemplate(entry, own));
  if (isPlainObject(value)) {
    return Object.entries(value).some(([key, entry]) => !IDENTITY_KEY.test(key) && carriesFilmTemplate(entry, own));
  }
  return false;
};

// The backend names every template ad set "…_THEATRICAL". Without the token,
// the page's ad-set label formatter adds the campaign's own category token.
const withoutTheatricalLabelToken = (label) =>
  String(label).replace(THEATRICAL_LABEL_TOKEN, "").replace(/^_+|_+$/g, "");

// One section, as the page should show it. Copy items carrying film language
// go whole; a row of the campaign's own goes only when its name was film
// template; every other film line goes on its own.
const withoutFilmTemplate = (value, own, spec, at = "", key = "") => {
  if (typeof value === "string") {
    if (key === UNIFIED_LABEL_KEY) return withoutTheatricalLabelToken(value);
    return carriesFilmTemplate(value, own) ? undefined : value;
  }
  if (Array.isArray(value)) {
    const copyItems = spec.copyItems === "*" || (Array.isArray(spec.copyItems) && spec.copyItems.includes(at));
    return value
      .filter((entry) => !(copyItems && isPlainObject(entry) && carriesFilmTemplate(entry, own)))
      .map((entry) => {
        if (!isPlainObject(entry)) return withoutFilmTemplate(entry, own, spec, at, key);
        const kept = withoutFilmTemplate(entry, own, spec, at, key);
        const lostItsName = Object.keys(entry).some(
          (name) => NAME_KEY.test(name) && typeof entry[name] === "string" && !(name in kept)
        );
        return lostItsName ? undefined : kept;
      })
      .filter((entry) => entry !== undefined);
  }
  if (isPlainObject(value)) {
    const next = {};
    for (const [entryKey, entry] of Object.entries(value)) {
      const entryAt = at ? `${at}.${entryKey}` : entryKey;
      if ((IDENTITY_KEY.test(entryKey) && entryKey !== UNIFIED_LABEL_KEY) || (!at && (spec.keep || []).includes(entryKey))) {
        next[entryKey] = entry;
        continue;
      }
      const kept = withoutFilmTemplate(entry, own, spec, entryAt, entryKey);
      if (kept !== undefined) next[entryKey] = kept;
    }
    return next;
  }
  return value;
};

const omitFromSections = (holder, own) => {
  if (!isPlainObject(holder)) return holder;
  const next = { ...holder };
  for (const key of WITHHELD_PAYLOAD_SECTIONS) delete next[key];
  if (isPlainObject(next.chartReady)) {
    next.chartReady = { ...next.chartReady };
    for (const key of WITHHELD_CHART_READY_KEYS) delete next.chartReady[key];
  }
  // A release-phase read the backend built as theatrical (opening window,
  // holdover markets) is film template from end to end.
  if (isPlainObject(next.releasePhaseIntelligence) && next.releasePhaseIntelligence.isTheatrical === true) {
    delete next.releasePhaseIntelligence;
  }
  for (const [key, spec] of Object.entries(TEMPLATE_SECTIONS)) {
    if (isPlainObject(next[key]) || Array.isArray(next[key])) next[key] = withoutFilmTemplate(next[key], own, spec);
  }
  return next;
};

// The campaign a General page renders. Any other vertical: the same object.
export const omitTheatricalTemplate = (campaign, advertiserVertical) => {
  if (!isPlainObject(campaign) || !campaignReceivesGeneralTemplate(advertiserVertical)) return campaign;
  const own = campaignInputValues(campaign);
  const next = omitFromSections(campaign, own);
  if (isPlainObject(campaign.sections)) next.sections = omitFromSections(campaign.sections, own);
  return next;
};
